import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import esriRequest from '@arcgis/core/request';
import AttachmentQuery from '@arcgis/core/rest/support/AttachmentQuery';

export interface KommunaltGalleriSettings {
    serviceUrl: string;
}

export interface KommunaltGalleriItem {
    objectId: number;
    tittel: string;
    beskrivelse: string | null;
    /** Ephemeral blob URL for current-session display (handles ArcGIS auth). */
    bilde_url: string | null;
    /** Permanent ArcGIS attachment URL for cross-session persistence. */
    bilde_permanent_url: string | null;
    creationDate: number | null;
    creator: string | null;
    editDate: number | null;
    editor: string | null;
}

export interface KommunaltGalleriInput {
    tittel: string;
    beskrivelse?: string | null;
    bildefil?: File | null;
}

interface AttachmentResult {
    blobUrl: string;
    permanentUrl: string;
}

export class KommunaltGalleriService {
    private settings: KommunaltGalleriSettings | null = null;
    private _layer: FeatureLayer | null = null;

    configure(settings: KommunaltGalleriSettings) {
        this.settings = settings;
        this._layer = null;
    }

    get isConfigured(): boolean {
        return !!this.settings?.serviceUrl;
    }

    private getLayer(): FeatureLayer {
        if (!this.settings?.serviceUrl) throw new Error('KommunaltGalleriService: serviceUrl not configured');
        this._layer ??= new FeatureLayer({ url: this.settings.serviceUrl });
        return this._layer;
    }

    private fromAttributes(attrs: Record<string, unknown>, attachment: AttachmentResult | null): KommunaltGalleriItem {
        return {
            objectId: attrs['OBJECTID'] as number,
            tittel: (attrs['tittel'] as string) ?? '',
            beskrivelse: (attrs['beskrivelse'] as string) ?? null,
            bilde_url: attachment?.blobUrl ?? null,
            bilde_permanent_url: attachment?.permanentUrl ?? null,
            creationDate: (attrs['CreationDate'] as number) ?? null,
            creator: (attrs['Creator'] as string) ?? null,
            editDate: (attrs['EditDate'] as number) ?? null,
            editor: (attrs['Editor'] as string) ?? null,
        };
    }

    private async fetchAttachmentUrls(objectIds: number[]): Promise<Map<number, AttachmentResult>> {
        if (objectIds.length === 0) return new Map();
        const serviceUrl = this.settings?.serviceUrl ?? '';
        const urlMap = new Map<number, AttachmentResult>();
        const result = await this.getLayer().queryAttachments(new AttachmentQuery({ objectIds }));
        await Promise.all(
            Object.entries(result).map(async ([objectIdStr, infos]) => {
                const attachments = infos as Array<{ id: number }>;
                if (attachments.length === 0) return;
                const latest = attachments.reduce((a, b) => (a.id > b.id ? a : b));
                const attachmentPath = `/${objectIdStr}/attachments/${latest.id}`;
                const adminFetchUrl = `${serviceUrl}${attachmentPath}`;
                try {
                    const response = await esriRequest(adminFetchUrl, { responseType: 'blob' });
                    urlMap.set(Number(objectIdStr), {
                        blobUrl: URL.createObjectURL(response.data as Blob),
                        permanentUrl: attachmentPath,
                    });
                } catch {
                    // show item without image if fetch fails
                }
            }),
        );
        return urlMap;
    }

    private async uploadAttachment(objectId: number, file: File): Promise<AttachmentResult> {
        const serviceUrl = this.settings?.serviceUrl ?? '';
        const formData = new FormData();
        formData.append('attachment', file, file.name);
        formData.append('f', 'json');

        const response = await esriRequest(`${serviceUrl}/${objectId}/addAttachment`, {
            method: 'post',
            body: formData,
            responseType: 'json',
        });

        const data = response.data as {
            addAttachmentResult?: { objectId: number; success: boolean };
            error?: { message: string };
        };
        if (data.error) throw new Error(data.error.message);
        if (!data.addAttachmentResult?.success) throw new Error('Bildeoppasting feilet');

        const attachmentId = data.addAttachmentResult.objectId;
        return {
            blobUrl: URL.createObjectURL(file),
            permanentUrl: `/${objectId}/attachments/${attachmentId}`,
        };
    }

    async fetchAttachmentBlobUrl(permanentUrl: string): Promise<string | null> {
        try {
            await this.getLayer().load();
            const fullUrl = permanentUrl.startsWith('/')
                ? `${this.settings?.serviceUrl ?? ''}${permanentUrl}`
                : permanentUrl;
            const response = await esriRequest(fullUrl, { responseType: 'blob' });
            return URL.createObjectURL(response.data as Blob);
        } catch {
            return null;
        }
    }

    async fetchKommunaltGalleri(): Promise<KommunaltGalleriItem[]> {
        const layer = this.getLayer();
        await layer.load();
        const oidField = layer.objectIdField;

        const q = layer.createQuery();
        q.where = '1=1';
        q.outFields = ['*'];
        q.orderByFields = ['CreationDate DESC'];
        const result = await layer.queryFeatures(q);

        const objectIds = result.features.map((f) => f.attributes[oidField] as number);
        let attachmentUrls = new Map<number, AttachmentResult>();
        try {
            attachmentUrls = await this.fetchAttachmentUrls(objectIds);
        } catch {
            // items shown without images if attachment fetch fails
        }

        return result.features.map((f) => {
            const objectId = f.attributes[oidField] as number;
            return this.fromAttributes({ ...f.attributes, OBJECTID: objectId }, attachmentUrls.get(objectId) ?? null);
        });
    }

    async addKommunaltGalleriItem(input: KommunaltGalleriInput): Promise<KommunaltGalleriItem> {
        const graphic = new Graphic({
            attributes: {
                tittel: input.tittel,
                beskrivelse: input.beskrivelse ?? null,
            },
        });
        const result = await this.getLayer().applyEdits({ addFeatures: [graphic] });
        const addResult = result?.addFeatureResults?.[0];
        if (!addResult || addResult.error) {
            throw new Error(addResult?.error?.message ?? 'Feil ved oppretting av element');
        }
        const objectId = addResult.objectId;
        if (objectId == null) throw new Error('Fikk ikke objectId fra server');

        let attachment: AttachmentResult | null = null;
        if (input.bildefil) {
            attachment = await this.uploadAttachment(objectId, input.bildefil);
        }

        const q = this.getLayer().createQuery();
        q.where = `OBJECTID = ${objectId}`;
        q.outFields = ['*'];
        const refetch = await this.getLayer().queryFeatures(q);
        return this.fromAttributes({ ...refetch.features[0].attributes, OBJECTID: objectId }, attachment);
    }

    async updateKommunaltGalleriItem(
        item: Pick<KommunaltGalleriItem, 'objectId' | 'tittel' | 'beskrivelse'>,
        bildefil?: File | null,
    ): Promise<AttachmentResult | null> {
        let attachment: AttachmentResult | null = null;

        if (bildefil) {
            attachment = await this.uploadAttachment(item.objectId, bildefil);
        }

        const graphic = new Graphic({
            attributes: {
                OBJECTID: item.objectId,
                tittel: item.tittel,
                beskrivelse: item.beskrivelse ?? null,
            },
        });
        const result = await this.getLayer().applyEdits({ updateFeatures: [graphic] });
        const updateResult = result?.updateFeatureResults?.[0];
        if (updateResult?.error) throw new Error(updateResult.error.message);

        return attachment;
    }

    async deleteKommunaltGalleriItem(objectId: number): Promise<void> {
        const result = await this.getLayer().applyEdits({
            deleteFeatures: [{ objectId } as unknown as Graphic],
        });
        const deleteResult = result?.deleteFeatureResults?.[0];
        if (deleteResult?.error) throw new Error(deleteResult.error.message);
    }
}
